import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { PageShell } from '../components/PageShell.js';

export function AdminLoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/admin';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  if (auth.status === 'authenticated') {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setSubmitting(true);
    try {
      if (auth.status === 'new-password') {
        await auth.finishNewPassword(newPassword);
      } else {
        await auth.login(email.trim(), password);
      }
    } catch {
      setError('Sign in failed. Check your email and password, then try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell>
      <section className="card mx-auto w-full max-w-md">
        <h1 className="heading-xl text-crimson">Administrator sign in</h1>
        <p className="mt-2 text-sm text-charcoal/80">
          Access is limited to the FOIS Administrators group.
        </p>
        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          {auth.status !== 'new-password' ? (
            <>
              <div>
                <label className="field-label" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  className="field-input"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="field-input"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
            </>
          ) : (
            <div>
              <label className="field-label" htmlFor="new-password">
                Choose a new password
              </label>
              <input
                id="new-password"
                name="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                className="field-input"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </div>
          )}
          {error ? (
            <p className="text-sm font-semibold text-crimson" role="alert">
              {error}
            </p>
          ) : null}
          <button className="btn-primary w-full" type="submit" disabled={submitting}>
            {submitting
              ? 'Signing in…'
              : auth.status === 'new-password'
                ? 'Save password'
                : 'Sign in'}
          </button>
        </form>
      </section>
    </PageShell>
  );
}
